import type { ReactNode } from 'react';
import { stationState } from '@lw/shared/indicator';
import { useRuntime } from '../runtime/useRuntime.js';
import { fmtMMSS } from '../app/helpers.js';

export function SyncChip(): ReactNode {
  const { rt } = useRuntime();
  const ind = rt.client.indicator.current();
  const lbl = rt.client.indicator.label();
  const cls = ind.colour === 'GREEN' ? 'ok' : ind.colour === 'ORANGE' ? 'warn' : 'bad';
  return (
    <span className={`chip ${cls}`}>
      {ind.colour === 'RED' ? 'Link lost' : `${lbl.label} ${lbl.value}`.trim()}
    </span>
  );
}

/** The counterpart's link as the server reports it (UAV sees FDC, FDC sees UAV). */
export function PeerChip(): ReactNode {
  const { rt } = useRuntime();
  const role = rt.id.role;
  const peer = role === 'UAV' ? 'FDC' : role === 'FDC' ? 'UAV' : null;
  if (!peer) return null;
  const st = rt.client.peerFresh() ? stationState(rt.client.stations[peer]) : { state: 'STALE' as const, detail: 'no data' };
  const cls = st.state === 'LIVE' ? 'ok' : st.state === 'LOST' ? 'bad' : 'warn';
  return (
    <span className={`chip ${cls}`} data-testid="peer-chip">
      {peer}: {st.state === 'STALE' ? 'no data' : st.state.toLowerCase()} {st.state === 'STALE' ? '' : st.detail}
    </span>
  );
}

export function EstimateChip(): ReactNode {
  const { rt, state } = useRuntime();
  const s = state.snapshot;
  if (!s?.expected) return null;
  const now = rt.client.chrono.serverNow();
  const elapsed = now === null ? 0 : now - s.expected.startedAt;
  const over = elapsed > s.expected.minutes * 60_000;
  return (
    <span className={`chip ${over ? 'warn' : ''}`} data-testid="estimate">
      FDC estimate <span className="v">{s.expected.minutes} min</span> · elapsed <span className="v">{fmtMMSS(elapsed)}</span>
    </span>
  );
}

export function ActedBy({ by }: { by: string | null }): ReactNode {
  return by ? <div className="actedby">acted by {by}</div> : null;
}

export function SolutionSummary(): ReactNode {
  const { state } = useRuntime();
  const s = state.snapshot;
  const sol = s?.solution;
  if (!s || !sol) return null;
  return (
    <dl className="kv">
      <dt>Target</dt><dd>{s.target?.value}</dd>
      <dt>Laser code</dt><dd>{sol.laserCode}</dd>
      <dt>Flight time</dt><dd>{sol.shellFlightTimeS} s</dd>
      <dt>Laser on</dt><dd>{sol.laserOnLeadS} s before impact</dd>
      <dt>Gun-target line</dt><dd>{sol.gtl ? `${sol.gtl.value} ${sol.gtl.unit}` : '—'}</dd>
    </dl>
  );
}

export function ReadyAge(): ReactNode {
  const { rt, state } = useRuntime();
  const at = state.snapshot?.uav.readyAt ?? null;
  const now = rt.client.chrono.serverNow();
  return <span className="mono">{at === null || now === null ? '—' : `${Math.max(0, Math.floor((now - at) / 1000))} s`}</span>;
}
